import React, { useEffect, useRef } from 'react'
import { KBSpin } from '@/components/atoms'

// Extend Window interface for CAPTCHA globals
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          theme?: string
          callback?: (token: string) => void
          'expired-callback'?: () => void
          'error-callback'?: () => void
        }
      ) => string
      remove: (widgetId: string) => void
    }
    grecaptcha?: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

interface CaptchaWidgetProps {
  /** Site key for Turnstile/reCAPTCHA */
  siteKey: string
  /** CAPTCHA provider type */
  provider: 'turnstile' | 'recaptcha'
  /** Callback when verification succeeds */
  onVerify: (token: string) => void
  /** Callback when verification expires */
  onExpire?: () => void
  /** Callback when verification fails */
  onError?: () => void
  /** Theme for the widget */
  theme?: 'light' | 'dark' | 'auto'
}

/**
 * CAPTCHA Widget component that renders Cloudflare Turnstile or Google reCAPTCHA
 *
 * This component dynamically loads the appropriate CAPTCHA script and renders the widget.
 *
 * For Turnstile documentation: https://developers.cloudflare.com/turnstile/
 * For reCAPTCHA documentation: https://developers.google.com/recaptcha/docs/v3
 */
export const CaptchaWidget: React.FC<CaptchaWidgetProps> = ({
  siteKey,
  provider,
  onVerify,
  onExpire,
  onError,
  theme = 'light',
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!siteKey || !containerRef.current) return

    const loadScript = (src: string, id: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
          resolve()
          return
        }
        const script = document.createElement('script')
        script.id = id
        script.src = src
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Failed to load ${id}`))
        document.head.appendChild(script)
      })
    }

    const initTurnstile = async () => {
      await loadScript(
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
        'cf-turnstile-script'
      )

      // Wait for Turnstile to be available
      const waitForTurnstile = (): Promise<void> => {
        return new Promise((resolve) => {
          const check = () => {
            if (window.turnstile) {
              resolve()
            } else {
              setTimeout(check, 100)
            }
          }
          check()
        })
      }

      await waitForTurnstile()

      const turnstile = window.turnstile
      if (containerRef.current && !widgetIdRef.current && turnstile) {
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: onVerify,
          'expired-callback': onExpire,
          'error-callback': onError,
        })
      }
    }

    const initRecaptcha = async () => {
      await loadScript(
        `https://www.google.com/recaptcha/api.js?render=${siteKey}`,
        'recaptcha-script'
      )

      // Wait for grecaptcha to be available
      const waitForRecaptcha = (): Promise<void> => {
        return new Promise((resolve) => {
          const check = () => {
            if (window.grecaptcha?.ready) {
              resolve()
            } else {
              setTimeout(check, 100)
            }
          }
          check()
        })
      }

      await waitForRecaptcha()

      const grecaptcha = window.grecaptcha
      if (!grecaptcha) return
      grecaptcha.ready(() => {
        grecaptcha
          .execute(siteKey, { action: 'register' })
          .then((token: string) => {
            onVerify(token)
          })
          .catch(() => {
            onError?.()
          })
      })
    }

    if (provider === 'turnstile') {
      initTurnstile().catch((err) => {
        console.error('Failed to initialize Turnstile:', err)
        onError?.()
      })
    } else if (provider === 'recaptcha') {
      initRecaptcha().catch((err) => {
        console.error('Failed to initialize reCAPTCHA:', err)
        onError?.()
      })
    }

    // Cleanup
    return () => {
      if (provider === 'turnstile' && widgetIdRef.current) {
        try {
          const turnstile = window.turnstile
          if (turnstile) {
            turnstile.remove(widgetIdRef.current)
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null
      }
    }
  }, [siteKey, provider, theme, onVerify, onExpire, onError])

  // For reCAPTCHA v3, the widget is invisible, so we don't need to render anything
  if (provider === 'recaptcha') {
    return null
  }

  return <div ref={containerRef} className="tw-min-h-[65px]" />
}

/**
 * Wrapper component that handles loading state and conditional rendering
 */
interface CaptchaWidgetWrapperProps {
  /** Whether CAPTCHA is enabled */
  enabled: boolean
  /** Whether config is still loading */
  loading?: boolean
  /** Site key for the widget */
  siteKey?: string
  /** Provider type */
  provider?: 'turnstile' | 'recaptcha'
  /** Verification callback */
  onVerify: (token: string) => void
  /** Expiry callback */
  onExpire?: () => void
  /** Error callback */
  onError?: () => void
}

export const CaptchaWidgetWrapper: React.FC<CaptchaWidgetWrapperProps> = ({
  enabled,
  loading,
  siteKey,
  provider,
  onVerify,
  onExpire,
  onError,
}) => {
  if (loading) {
    return (
      <div className="tw-min-h-[65px]">
        <KBSpin size="small" />
      </div>
    )
  }

  if (!enabled || !siteKey || !provider) {
    return null
  }

  return (
    <CaptchaWidget
      siteKey={siteKey}
      provider={provider}
      onVerify={onVerify}
      onExpire={onExpire}
      onError={onError}
    />
  )
}

export default CaptchaWidget
